import { getIncidentTypeLabel } from "@/lib/utils/matters/incident-classification";
import {
  ActionConfirmationModal,
  AppModal,
  Button,
  Form,
  FormSubmitButton,
  MultiselectFormField,
  SimpleSelectFormField,
  TextFormField,
} from "@/components/ui";
import { FormLoadingOverlay } from "@/components/ui/Form/FormLoadingOverlay";
import { CurrencyFormField } from "@/components/ui/FormFields/CurrencyFormField/CurrencyFormField";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { useUser } from "@/components/UserProvider";
import {
  useCreateMatterDefaultExpense,
  useDeleteMatterDefaultExpense,
  useUpdateMatterDefaultExpense,
} from "@/lib/data/react-query/matters/matter-default-expenses";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query/matters/matter-settings";
import { DefaultMatterExpenseSchema } from "@/lib/data/schemas";
import { MATTER_EXPENSE_CATEGORY_OPTIONS } from "@/lib/data/static/matters/expenseStatics";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { type DefaultMatterExpense } from "@/lib/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { toLower } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import type { z } from "zod";

const DEFAULT_MATTER_EXPENSE_FORM_ID = "matter-default-expense-form";

interface AddOrEditDefaultMatterExpenseModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  editing: boolean;
}

type ConditionalProps =
  | {
      isReadOnly?: boolean;
      editing: true;
      activeDefaultMatterExpense: DefaultMatterExpense;
    }
  | {
      isReadOnly?: never;
      editing: false;
      activeDefaultMatterExpense?: never;
    };

const formSchema = DefaultMatterExpenseSchema.pick({
  label: true,
  category: true,
  incidentTypeIds: true,
  cost: true,
});

const defaultObject = {
  label: "",
  category: "",
  incidentTypeIds: [],
  cost: null,
};

export function AddOrEditDefaultMatterExpenseModal({
  activeDefaultMatterExpense,
  editing,
  isReadOnly,
  open,
  setOpen,
}: AddOrEditDefaultMatterExpenseModalProps & ConditionalProps) {
  const { hasActionPermission } = useUser();
  const [isDeleting, setIsDeleting] = useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    values: editing
      ? {
          ...defaultObject,
          ...activeDefaultMatterExpense,
          incidentTypeIds: activeDefaultMatterExpense.incidentTypeIds,
        }
      : defaultObject,
    mode: "onSubmit",
  });

  const { data: matterSettingsData } = useQuery(
    retrieveMatterSettingsQueryOptions(),
  );
  const incidentTypes = matterSettingsData?.data.incidentTypes ?? [];
  const defaultExpenses = useMemo(
    () => matterSettingsData?.data.defaultExpenses ?? [],
    [matterSettingsData],
  );

  useEffect(() => {
    if (!open) {
      form.reset();
    }
  }, [open, form]);

  const deleteMatterDefaultExpense = useDeleteMatterDefaultExpense();
  const createMatterDefaultExpense = useCreateMatterDefaultExpense();
  const updateMatterDefaultExpense = useUpdateMatterDefaultExpense();
  const isFormLoading =
    deleteMatterDefaultExpense.isPending ||
    createMatterDefaultExpense.isPending ||
    updateMatterDefaultExpense.isPending;

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    if (
      defaultExpenses.some(
        (expense) =>
          toLower(expense.label.trim()) === toLower(data.label.trim()) &&
          expense.id !== activeDefaultMatterExpense?.id,
      )
    ) {
      form.setError("label", {
        message: "Default matter expense label must be unique",
      });
      return;
    }
    if (editing) {
      await updateMatterDefaultExpense.mutateAsync({
        id: activeDefaultMatterExpense.id,
        dateModified: activeDefaultMatterExpense.dateModified,
        ...data,
      });
    } else {
      await createMatterDefaultExpense.mutateAsync({
        defaultMatterExpense: data,
      });
      form.reset();
    }
    setOpen(false);
  };

  const handleDelete = async () => {
    if (editing) {
      await deleteMatterDefaultExpense
        .mutateAsync({
          id: activeDefaultMatterExpense.id,
        })
        .then(() => {
          setIsDeleting(false);
        });
    }
    setOpen(false);
  };

  const modalTitle = editing
    ? "Update default matter expense"
    : "Create default matter expense";
  const modalDescription = editing
    ? "Update the default matter expense below"
    : "Create a new default matter expense to your firm";

  return (
    <>
      <Form {...form}>
        <AppModal
          open={open}
          onOpenChange={setOpen}
          title={modalTitle}
          description={modalDescription}
          loading={isFormLoading}
          isDirty={form.formState.isDirty}
          size="2xl"
          footer={
            <div className="flex w-full flex-wrap items-center gap-2">
              {!isReadOnly &&
                editing &&
                hasActionPermission(
                  ACTION_PERMISSION_KEYS.defaultMatterExpense,
                  ACTION_PERMISSION_VALUES.delete,
                ) && (
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => {
                      setIsDeleting(true);
                    }}
                  >
                    Delete{" "}
                  </Button>
                )}
              <div className="ml-auto flex gap-2">
                <FormCancelButton
                  label={isReadOnly ? "Close" : "Cancel"}
                  setOpen={setOpen}
                  isDirty={form.formState.isDirty}
                />
                {!isReadOnly ? (
                  <FormSubmitButton
                    submitFormId={DEFAULT_MATTER_EXPENSE_FORM_ID}
                  />
                ) : null}
              </div>
            </div>
          }
        >
          <form
            id={DEFAULT_MATTER_EXPENSE_FORM_ID}
            className="relative flex min-h-0 flex-1 flex-col"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            {isFormLoading ? <FormLoadingOverlay /> : null}
            <fieldset
              disabled={isReadOnly}
              className="grid min-h-0 grid-cols-1 gap-4 sm:grid-cols-2"
            >
              <TextFormField
                register={form.register("label")}
                label="Label*"
                dataTestId="matter-default-expense-label-input"
              />
              <SimpleSelectFormField
                register={form.register("category")}
                label="Category*"
                items={MATTER_EXPENSE_CATEGORY_OPTIONS}
                placeholder="Select a category"
              />
              <MultiselectFormField
                label="Incident types*"
                options={incidentTypes.map((incidentType) => ({
                  label: getIncidentTypeLabel(incidentType),
                  value: incidentType.id,
                }))}
                register={form.register("incidentTypeIds")}
              />
              <CurrencyFormField
                register={form.register("cost")}
                label="Cost*"
                dataTestId="matter-default-expense-cost-input"
              />
            </fieldset>
          </form>
        </AppModal>
      </Form>
      <ActionConfirmationModal
        title={`Are you sure you want to delete this matter default expense?`}
        open={isDeleting && !!activeDefaultMatterExpense}
        setOpen={setIsDeleting}
        afterActionFn={async () => {
          if (activeDefaultMatterExpense) {
            await handleDelete();
          }
        }}
      />
    </>
  );
}
